<template>
  <button
      :class="[
      'btn',
      `btn--${variant}`,
      { 'btn--block': block, 'is-loading': loading }
    ]"
      :disabled="disabled || loading"
      v-bind="$attrs"
  >
    <span class="btn__inner">
      <slot name="icon"/>
      <span v-if="hasDefaultSlot" class="btn__label">
        <slot/>
      </span>
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'

type Variant = 'primary' | 'blue' | 'outline' | 'gold' | 'purple' | 'transparent' | 'grey' | 'glass' | 'danger'

const slots = useSlots()

const hasDefaultSlot = computed(() => {
  return slots.default && slots.default().some(vnode => {
    return vnode.children && vnode.children.toString().trim() !== ''
  })
})

withDefaults(defineProps<{
  variant?: Variant
  block?: boolean
  disabled?: boolean
  loading?: boolean
}>(), {
  variant: 'primary',
  block: true,
  disabled: false,
  loading: false
})
</script>

<style lang="scss">
.btn {
  --txt: #fff;
  --bg: transparent;
  --bd: rgba(255, 255, 255, .85);
  width: 100%;
  appearance: none;
  border-radius: 18px;
  border: 2px solid var(--bd);
  color: var(--txt);
  cursor: pointer;
  transition: .2s ease;
  position: relative;
  animation: none;
  background: var(--bg);
  background-size: 200% 200%;
  background-position: 10% 70%;
  padding: 17px;
  overflow: hidden;

  @include media(768px){
    background-size: 150% 100%;
    background-position: 50% 50%;
  }

  &::before {
    content: '';
    width: calc(100% - 6px);
    height: calc(100% - 6px);
    background: transparent;
    position: absolute;
    top: 3px;
    left: 3px;
    border-radius: 12px;
    z-index: 0;
  }

  //&::after {
  //  content: '';
  //  position: absolute;
  //  top: 0;
  //  left: -100%;
  //  width: 50%;
  //  height: 100%;
  //  background: linear-gradient(
  //          120deg,
  //          rgba(255, 255, 255, 0) 0%,
  //          rgba(255, 255, 255, 0.6) 50%,
  //          rgba(255, 255, 255, 0) 100%
  //  );
  //  transform: skewX(-20deg) translateX(-100%);
  //  opacity: 0.3;
  //  animation: shine 5s infinite ease-in-out;
  //  z-index: 1;
  //  will-change: transform;
  //  pointer-events: none;
  //}

  .btn__inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    position: relative;
    z-index: 1
  }

  .btn__label {
    font-weight: 700;
    font-size: 20px;

    @include media(375px){
      font-size: 12px;
      font-weight: 500;
    }
  }


  &:hover {
    animation: gradientShift 3s ease-in-out infinite alternate;

    @include media(768px){
      animation: none;
    }
  }

  &:active, &:focus {
    animation: gradientShift 3s ease-in-out infinite alternate;
    @include media(768px){
      animation: none;
    }
  }

  &--primary {
    --bg: linear-gradient(80.74deg, #FF1569 0%, #C03AFF 100%),
    linear-gradient(245.03deg, rgba(255, 255, 255, 0) 9.6%, rgba(255, 255, 255, 0.152) 21.33%, rgba(255, 255, 255, 0) 28.59%, rgba(255, 255, 255, 0) 28.6%, rgba(255, 255, 255, 0) 49.81%, rgba(255, 255, 255, 0.158) 62.65%, rgba(255, 255, 255, 0) 71.03%);
    --txt: #FFFFFF;
    --bd: transparent;
  }

  &--blue {
    --bg: linear-gradient(252.45deg, #247DCF 0.27%, #62B4FF 21.86%, #247DCF 47.21%, #62B3FF 71.14%, #247DCF 90.22%);
  }

  &--purple {
    --bg: linear-gradient(252.45deg, #AA3FEC 0.27%, #DF89FE 21.86%, #AA3FEC 47.21%, #DF89FE 68.33%, #AA3FEC 90.39%);
  }

  &--outline {
    --txt: #FF1569;
    --bd: transparent;
    --bg: linear-gradient(80.74deg, #FF1569 0%, #C03AFF 100%),
    linear-gradient(245.03deg, rgba(255, 255, 255, 0) 9.6%, rgba(255, 255, 255, 0.152) 21.33%, rgba(255, 255, 255, 0) 28.59%, rgba(255, 255, 255, 0) 28.6%, rgba(255, 255, 255, 0) 49.81%, rgba(255, 255, 255, 0.158) 62.65%, rgba(255, 255, 255, 0) 71.03%);

    &::before {
      background: #FFFFFF;
    }
  }

  &--gold {
    --bg: linear-gradient(266.68deg, #E0BA5C -1.34%, #FDEC97 27.13%, #C99533 55.06%, #F9F09A 101.8%);

  }

  &--transparent {
    --bg: transaprent;
    --txt: #454545;
  }

  &--grey {
    --bg: linear-gradient(84.19deg, #454545 3.33%, #A6A6A6 24.74%, #0B1C3F 50.22%, #969696 75.2%, #0B1C3F 100.17%);
    --txt: #FFFFFF;
  }

  &--danger {
    --txt: #FFFFFF;
    background-color: #ED4C5C !important;
  }

  &:disabled {
    opacity: .6;
    cursor: not-allowed;
  }
}

@keyframes gradientShift {
  0% {
    background-position: 10% 70%;;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 10% 70%;
  }
}

@keyframes shine {
  0% {
    transform: skewX(-20deg) translateX(-100%);
  }
  60% {
    transform: skewX(-20deg) translateX(130%);
  }
  100% {
    transform: skewX(-20deg) translateX(130%);
  }
}

</style>
